import { GetStaticPropsContext } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import ArticlePreview from '../../components/ArticlePreview'
import { CarsGrid } from '../../components/Cars/styled'
import Container from '../../components/Container'
import Footer from '../../components/Footer'
import Header from '../../components/Header'
import JumbotronBox from '../../components/Jumbotron/JumbotronBox'
import Typography from '../../components/Typography'
import blog from '../../services/blog'
import NotFoundPage from '../404'

const MainImage = styled.img`
  width: 100%;
  margin: 0 auto;
  max-height: 850px;
  object-fit: cover;
  border-radius: 5px;
`

const ContentContainer = styled.div`
  padding: 0 15px;
  margin: 0 auto 20px;
  max-width: 930px;

  h1 {
    font-size: 44px;
    font-weight: 700;
    margin: 50px 0;
  }

  h2 {
    font-size: 33px;
    font-weight: 700;
    margin: 30px 0 20px;
  }

  p {
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
    margin: 15px 0;
  }

  img {
    width: 100%;
    border-radius: 5px;
    margin: 5px 0;
  }

  ol,
  ul {
    padding-left: 15px;

    li {
      height: 24px;
    }
  }
`

const CTAContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #fafafb;
  padding: 50px;
  border-radius: 5px;
  margin-bottom: 50px;

  @media (max-width: 600px) {
    padding: 30px;
  }
`

const BlogPage = ({ slug, locale }) => {
  const { t } = useTranslation('common')
  const router = useRouter()
  const article = blog.getArticleBySlug(slug as string)

  if (!article || article.language !== locale) {
    return <NotFoundPage />
  }

  const { Component } = article

  const onSubmit = ({ range }: { range: [string, string] }) => {
    router.push(`/?start=${range[0]}&end=${range[1]}`)
  }

  return (
    <>
      <Head>
        <title>{article.title} | DreamCar</title>
        <meta name="description" content={article.description} />
      </Head>
      <Header blog />
      <Container medium>
        <MainImage src={article.thumbnail} alt={article.title} />
      </Container>
      <ContentContainer>
        <h1>{article.title}</h1>
        <Component />
        <CTAContainer>
          <Typography.H3>{t('need_car')}</Typography.H3>
          <JumbotronBox onSubmit={onSubmit} />
        </CTAContainer>
      </ContentContainer>
      <ContentContainer>
        <Typography.H3>Другие стать</Typography.H3>
        <CarsGrid lgRepeat={3}>
          {blog.getArticlesByLanguage(locale, 3, article.slug).map((relatedArticle) => (
            <ArticlePreview key={relatedArticle.slug} article={relatedArticle} />
          ))}
        </CarsGrid>
      </ContentContainer>
      <Footer blog />
    </>
  )
}

export const getStaticPaths = () => {
  const articles = blog.getAllArticles()
  const paths = articles.map(({ slug, language }) => `/${language}/blog/${slug}`)

  return {
    paths,
    fallback: true,
  }
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  return {
    props: {
      slug: context.params.slug,
      locale: context.locale,
      ...(await serverSideTranslations(context.locale, ['common'])),
    },
  }
}

export default BlogPage
